import React from "react";
import TopBanner from "../components/TopBanner";
import { CTAButton, CTAButtonWrapper } from "../components/HeroSection";
import { ContentWrapper } from "./InsuranceGuidance";
import { Card, Cards, Icon } from "../components/WhoWeAre";
import { CardText, CardTitle } from "../components/WhyChooseUS";
import styled from "styled-components";
import FAQ from "../components/FAQ";

const MotorInsurance = ({ openModal }) => {
  React.useEffect(() => {
    document.title =
      "Motor Insurance Guide – Meaning, Types, Coverage, Eligibility & Legal Rules in India | Wealthies";
  }, []);

  const faqData = [
    {
      question: "Is motor insurance mandatory in India?",
      answer:
        "Yes, at least third-party motor insurance is required for vehicles under the Motor Vehicles Act, 1988.",
    },
    {
      question: "What happens if I drive without motor insurance?",
      answer:
        "Driving without valid insurance may lead to penalties or fines under motor vehicle regulations.",
    },
    {
      question: "Does motor insurance cover natural disasters?",
      answer:
        "Some comprehensive policies may cover damages caused by natural disasters such as floods or storms, depending on policy terms.",
    },
    {
      question: "Can motor insurance be renewed online?",
      answer:
        "Many insurance companies allow policy renewal online, subject to verification and policy conditions.",
    },
  ];
  return (
    <>
      <TopBanner content={"Motor Insurance"} />
      <ContentWrapper>
        <div className="text-content">
          <h2>Introduction</h2>
          <p>
            Motor insurance is designed to provide financial protection against
            damage, accidents, theft, or third-party liabilities involving
            vehicles.
          </p>
          <p>
            In India, motor insurance is also a legal requirement for vehicle
            owners under the Motor Vehicles Act, 1988. Every vehicle owner must
            have at least a third-party motor insurance policy to legally drive
            on public roads.
          </p>
          <p>
            Understanding how motor insurance works can help vehicle owners
            protect themselves financially and comply with legal requirements.
          </p>
          <p>
            This guide explains the basics of motor insurance, including types
            of coverage, eligibility, claim process, and important factors to
            consider before purchasing a policy.
          </p>

          <h2>
            What is <span>Motor Insurance</span>
          </h2>
          <p>
            Motor insurance is a type of insurance policy that provides
            financial coverage for vehicles against certain risks such as
            accidents, theft, fire, natural disasters, or third-party
            liabilities.
          </p>
          <p>
            Vehicle owners pay a premium to the insurance company, and the
            insurer may provide financial compensation for covered damages or
            liabilities, subject to the policy terms and conditions.
          </p>
          <p>Motor insurance policies may cover:</p>
          <ul>
            <li>Damage to the insured vehicle</li>
            <li>Third-party injuries or property damage</li>
            <li>Theft or loss of vehicle</li>
            <li>Natural calamities such as floods or storms</li>
          </ul>
          <p>Coverage depends on the policy type selected.</p>

          <h2>
            Why <span>Motor Insurance</span> is Important
          </h2>
          <p>
            Motor insurance plays an important role in protecting vehicle owners
            from financial losses due to accidents or unexpected events.
          </p>
          <p>
            Some reasons why vehicle owners consider motor insurance include:
          </p>
          <ul>
            <li>
              Complying with legal requirements for driving on public roads
            </li>
            <li>Financial protection against vehicle damage</li>
            <li>Protection against third-party liability claims</li>
            <li>Assistance in covering repair expenses after accidents</li>
          </ul>
          <p>
            Motor insurance can help reduce financial stress caused by
            vehicle-related incidents.
          </p>

          <h2>
            Types of <span>Motor Insurance</span> in India
          </h2>
          <p>
            Different types of motor insurance policies are available depending
            on the level of coverage required.
          </p>

          <h5>Third-Party Insurance</h5>
          <p>
            Third-party insurance is the minimum legally required insurance
            under the Motor Vehicles Act, 1988.
          </p>
          <p>This policy generally covers:</p>
          <ul>
            <li>Injury or death of a third party</li>
            <li>Damage caused to third-party property</li>
          </ul>
          <p>
            However, it usually does not cover damage to the insured vehicle
            itself.
          </p>

          <h5>Comprehensive Motor Insurance</h5>
          <p>
            Comprehensive motor insurance generally provides broader coverage.
          </p>
          <p>This type of policy may cover:</p>
          <ul>
            <li>Third-party liabilities</li>
            <li>Damage to the insured vehicle</li>
            <li>Theft or loss of the vehicle</li>
            <li>Natural disasters such as floods or storms</li>
          </ul>
          <p>Coverage details depend on the insurer and policy terms.</p>

          <h5>Standalone Own Damage Policy</h5>
          <p>
            A standalone own damage policy covers damages to the insured vehicle
            but does not include third-party liability.
          </p>
          <p>
            Vehicle owners typically combine this with a separate third-party
            policy.
          </p>

          <h5>Eligibility for Motor Insurance</h5>
          <p>
            Most vehicle owners in India can purchase motor insurance if they
            meet basic eligibility requirements.
          </p>
          <p>Common eligibility conditions include:</p>
          <ul>
            <li>Ownership of a registered vehicle</li>
            <li>Valid vehicle registration documents</li>
            <li>Identity proof of the vehicle owner</li>
          </ul>
          <p>
            Insurance companies may also request additional documents depending
            on policy requirements.
          </p>

          <h2>
            Factors That <span>Affect Motor Insurance</span> Premiums
          </h2>
          <p>Motor insurance premiums may depend on several factors.</p>
          <Cards>
            <Card>
              <Icon className="fas fa-car"></Icon>
              <CardTitle>Vehicle Type</CardTitle>
              <CardText>
                Premiums may vary depending on whether the vehicle is a car,
                bike, or commercial vehicle.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-calendar-alt"></Icon>
              <CardTitle>Vehicle Age</CardTitle>
              <CardText>
                Older vehicles may have different premium calculations compared
                to newer vehicles.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-tachometer-alt"></Icon>
              <CardTitle>Engine Capacity</CardTitle>
              <CardText>
                Vehicles with higher engine capacity may have higher insurance
                premiums.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-map-marker-alt"></Icon>
              <CardTitle>Location of Registration</CardTitle>
              <CardText>
                Premiums may vary depending on the city or region where the
                vehicle is registered.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-award"></Icon>
              <CardTitle>No Claim Bonus (NCB)</CardTitle>
              <CardText>
                If the policyholder does not make a claim during the policy
                period, insurers may provide a No Claim Bonus, which can reduce
                future premiums.
              </CardText>
            </Card>
          </Cards>
          <h2>
            How <span>Motor Insurance</span> Claims Work
          </h2>
          <p>
            If a vehicle accident or damage occurs, the policyholder can
            initiate a claim with the insurance company.
          </p>
          <p>A typical motor insurance claim process may include:</p>
          <ul>
            <li>Informing the insurance company about the incident</li>
            <li>
              Submitting required documents such as policy details and vehicle
              information
            </li>
            <li>Inspection of the vehicle damage by the insurer or surveyor</li>
            <li>Repair process at an authorised garage or service center</li>
          </ul>
          <p>
            The claim settlement depends on the insurer’s assessment and policy
            terms.
          </p>

          <h2>
            Common <span>Mistakes People Make</span> When Buying Motor Insurance
          </h2>
          <Cards>
            <Card>
              <Icon className="fas fa-tags"></Icon>
              <CardTitle>Choosing Only the Cheapest Policy</CardTitle>
              <CardText>
                Selecting insurance based only on price may lead to inadequate
                coverage.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-file-alt"></Icon>
              <CardTitle>Ignoring Policy Coverage Details</CardTitle>
              <CardText>
                It is important to review what damages are covered and what
                exclusions apply.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-question-circle"></Icon>
              <CardTitle>Not Understanding Claim Procedures</CardTitle>
              <CardText>
                Understanding the claim process helps avoid confusion during
                accidents.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-sync-alt"></Icon>
              <CardTitle>Forgetting Policy Renewal</CardTitle>
              <CardText>
                Motor insurance policies require periodic renewal to maintain
                coverage.
              </CardText>
            </Card>
          </Cards>
          <WarningBox>
            <i className="fas fa-exclamation-triangle"></i>
            <span>
              Driving without valid insurance may lead to legal penalties.
            </span>
          </WarningBox>

          <h2>
            Frequently <span>Asked</span> Questions
          </h2>
          <FAQ faqData={faqData} />

          <h2>
            How <span>Wealthies</span> Helps
          </h2>
          <p>
            Wealthies aims to simplify insurance awareness by helping
            individuals understand insurance concepts in simple language.
          </p>
          <p>Through our platform, users can:</p>
          <ul>
            <li>Learn about different types of insurance policies</li>
            <li>Understand key factors before purchasing coverage</li>
            <li>
              Connect with licensed insurance professionals for further
              assistance
            </li>
            <li>Receive support guidance through Wealthies Shield</li>
          </ul>

          <p>Need help understanding motor insurance coverage?</p>
          <p>
            Our team can help explain the basics and connect you with licensed
            insurance professionals for further assistance.
          </p>
          <CTAButtonWrapper>
            <CTAButton onClick={openModal}>
              Get Free Insurance Guidance
            </CTAButton>
          </CTAButtonWrapper>
        </div>
      </ContentWrapper>
    </>
  );
};

export default MotorInsurance;

export const WarningBox = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 22px;
  border-radius: 14px;
  margin-top: 20px;
  margin-bottom: 60px;

  background: linear-gradient(
    135deg,
    rgba(255, 77, 79, 0.12),
    rgba(255, 77, 79, 0.04)
  );

  border: 1px solid rgba(255, 77, 79, 0.35);
  backdrop-filter: blur(6px);

  color: #ff6b6b;
  font-size: 15px;

  i {
    font-size: 20px;
  }
`;
